import { FirebaseStorage, connectStorageEmulator, getStorage } from 'firebase/storage'
import firebase_app from './firebase-config'

const DEFAULT_FIREBASE_STORAGE_EMULATOR_ORIGIN = 'http://127.0.0.1:9199'

export type GetAppStorageOptions = {
  bucketUrl?: string
  legacy_always_cloud?: boolean
}

const storageInstances = new Map<string, FirebaseStorage>()

function getStorageEmulatorUrl(): URL {
  return new URL(import.meta.env.VITE_LOCAL_STORAGE_EMULATOR_ORIGIN || DEFAULT_FIREBASE_STORAGE_EMULATOR_ORIGIN)
}

/**
 * Reuse the same emulator-vs-cloud decision anywhere behavior needs to match
 * the storage instance being returned for this callsite.
 */
export function usesStorageEmulator(options: GetAppStorageOptions = {}): boolean {
  const { legacy_always_cloud = false } = options
  return !legacy_always_cloud && import.meta.env.MODE === 'development'
}

/** REST v1 media upload URL for the Storage emulator.
 *
 * This is generic storage transport plumbing. user_files authorization still
 * belongs to the user_files API endpoints that choose the object key first.
 */
export function buildStorageEmulatorV1MediaUploadUrl(bucketRef: string, objectKey: string): string {
  const bucketId = bucketRef.replace(/^gs:\/\//, '')
  const origin = getStorageEmulatorUrl().origin
  return `${origin}/upload/storage/v1/b/${bucketId}/o?uploadType=media&name=${encodeURIComponent(objectKey)}`
}

function getStorageCacheKey(bucketUrl: string | undefined, useEmulator: boolean): string {
  const mode = useEmulator ? 'emulator' : 'cloud'
  const emulatorOrigin = useEmulator ? getStorageEmulatorUrl().origin : ''
  return `${mode}:${emulatorOrigin}:${bucketUrl ?? 'default'}`
}

/**
 * Returns a Firebase storage instance.
 * - default: new stack behavior (uses emulator in local development)
 * - legacy_always_cloud: true forces cloud-backed storage even in local development
 */
export function getAppStorage(options: GetAppStorageOptions = {}): FirebaseStorage {
  const { bucketUrl } = options
  const useEmulator = usesStorageEmulator(options)
  const cacheKey = getStorageCacheKey(bucketUrl, useEmulator)

  const cached = storageInstances.get(cacheKey)
  if (cached) {
    return cached
  }

  const storage = getStorage(firebase_app, bucketUrl)

  if (useEmulator) {
    const emulatorUrl = getStorageEmulatorUrl()
    connectStorageEmulator(storage, emulatorUrl.hostname, Number(emulatorUrl.port))
  }

  storageInstances.set(cacheKey, storage)
  return storage
}
